"use client";

import {
  type BgPattern,
  DEFAULT_BG_PATTERN,
  DEFAULT_DENSITY,
  DEFAULT_PALETTE,
  DEFAULT_THEME,
  type Density,
  type Palette,
  type SensTier,
  SENS_TIERS,
  type Theme,
} from "@/lib/palette";
import { useEffect } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";

type PaletteState = {
  palette: Palette;
  theme: Theme;
  density: Density;
  bgPattern: BgPattern;
  sensFilter: SensTier;
  sidebarCollapsed: boolean;
  hydrated: boolean;
  setPalette: (p: Palette) => void;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
  setDensity: (d: Density) => void;
  toggleDensity: () => void;
  setBgPattern: (v: BgPattern) => void;
  toggleBgPattern: () => void;
  setSensFilter: (s: SensTier) => void;
  cycleSensFilter: () => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (v: boolean) => void;
};

export const usePaletteStore = create<PaletteState>()(
  persist(
    (set, get) => ({
      palette: DEFAULT_PALETTE,
      theme: DEFAULT_THEME,
      density: DEFAULT_DENSITY,
      bgPattern: DEFAULT_BG_PATTERN,
      sensFilter: "internal",
      sidebarCollapsed: false,
      hydrated: false,
      setPalette: (palette) => set({ palette }),
      setTheme: (theme) => set({ theme }),
      toggleTheme: () => set({ theme: get().theme === "light" ? "dark" : "light" }),
      setDensity: (density) => set({ density }),
      toggleDensity: () =>
        set({ density: get().density === "compact" ? "comfortable" : "compact" }),
      setBgPattern: (bgPattern) => set({ bgPattern }),
      toggleBgPattern: () => set({ bgPattern: get().bgPattern === "on" ? "off" : "on" }),
      setSensFilter: (sensFilter) => set({ sensFilter }),
      cycleSensFilter: () => {
        const i = SENS_TIERS.indexOf(get().sensFilter);
        set({ sensFilter: SENS_TIERS[(i + 1) % SENS_TIERS.length] });
      },
      toggleSidebar: () => set({ sidebarCollapsed: !get().sidebarCollapsed }),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
    }),
    {
      name: "vc-palette",
      partialize: (s) => ({
        palette: s.palette,
        theme: s.theme,
        density: s.density,
        bgPattern: s.bgPattern,
        sensFilter: s.sensFilter,
        sidebarCollapsed: s.sidebarCollapsed,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) state.hydrated = true;
      },
    },
  ),
);

/** Subscribe to {palette, theme, density, bgPattern} and project them
 * onto <html>'s data-* attributes. Mounted exactly once via
 * <PaletteEffect /> in Providers. */
export function usePaletteEffect(): void {
  const palette = usePaletteStore((s) => s.palette);
  const theme = usePaletteStore((s) => s.theme);
  const density = usePaletteStore((s) => s.density);
  const bgPattern = usePaletteStore((s) => s.bgPattern);
  const hydrated = usePaletteStore((s) => s.hydrated);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    // Public share pages force their own palette + bg-pattern via the
    // inline script at the top of ``/share/[token]/page.tsx``. Without
    // this bail, React hydration would run this effect and stomp the
    // ``dekabrist`` + ``bg-pattern=off`` overrides back to the store's
    // "shu" default (which is what an anon browser holds — the store
    // is empty). Keep the guard narrow: any page that sets
    // ``data-share-page="true"`` owns its own palette state.
    if (root.getAttribute("data-share-page") === "true") return;
    root.setAttribute("data-palette", palette);
    root.setAttribute("data-theme", theme);
    root.setAttribute("data-density", density);
    root.setAttribute("data-bg-pattern", bgPattern);
    // Keep the legacy .dark class until all components migrate off it.
    root.classList.toggle("dark", theme === "dark");
  }, [palette, theme, density, bgPattern, hydrated]);
}
